import { AlertType, DocumentStatus } from 'briefpoint-client';
import Help from 'components/Help';
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { Check, Exclamation, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { DocumentClient } from 'services/DocumentService';
import { formatLongDate } from 'utils/dateUtils';
import ArchiveIcon from './ArchiveIcon';

interface LoadingIconProps {
  complete: boolean;
  error: boolean;
  fixInProgress: boolean;
}

function StatusIcon({ complete, fixInProgress, error }: LoadingIconProps) {
  return fixInProgress ? (
    <div className="fixInProgress">
      <ExclamationTriangleFill />
    </div>
  ) : error ? (
    <div className="error-icon">
      <Exclamation />
    </div>
  ) : complete ? (
    <div className="complete-icon">
      <Check />
    </div>
  ) : (
    <Spinner style={{ width: "1.3rem", height: "1.3rem" }} animation="border" variant="warning">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  );
}

interface Props {
  document: DocumentClient;
  isComplete: boolean;
  archiveDocument: (document: DocumentClient) => Promise<void>;
  processingType?: string;
}

export default function ProcessingBox({ document, isComplete, archiveDocument, processingType = 'Processing' }: Props) {
  const uploadedDateTime = formatLongDate(document.uploadDate);
  const filename = document.name;
  const error = document.status === DocumentStatus.Error;
  const fixInProgress = document.alert === AlertType.FixInProgress
  const containerClassName = fixInProgress ? fixInProgress : error ? 'error' : isComplete ? 'complete' : processingType;
  const heading = fixInProgress ? 'Fix in progress for' : error
    ? `Error ${processingType}`
    : isComplete
      ? `${processingType} complete:`
      : uploadedDateTime
        ? `Currently ${processingType}`
        : 'Uploading';

  return (
    <div id={`processing-box-${document.id}`} className={`processing-box ${containerClassName}`}>
      <StatusIcon complete={isComplete} fixInProgress={fixInProgress} error={error} />
      <div className="details">
        <div className="details">
          <div className="details-inner">
            <small className="details-text">{heading} <b>{filename}.</b></small>
            {fixInProgress ?
              <small className="header-two-wrap">
                Please check back later.
              </small>
              :
              <></>}
            {error && (
              <Help
                id={`${document.id}-error`}
                text="Briefpoint currently only supports Discovery Requests in the State of California. If your document meets that criteria, please let us know you encountered an issue using the Feedback button on the right."
              />
            )}
            {/* This text should be smaller than small  */}
            {uploadedDateTime && <small className="details-text">Uploaded {uploadedDateTime}.</small>}
          </div>
        </div>
      </div>
      {
        isComplete && (
          <div className="startedLink-wrapper">
            <Link
              className="btn btn-outline-secondary btn-sm wide"
              to={`/wizard/${document.id}/source-document-review?document_type=false`}
            >
              Get Started
            </Link>
          </div>
        )
      }
      <ArchiveIcon document={document} archiveDocument={archiveDocument} />
    </div >
  );
}